import { BRAND } from './branding.js';
import { DEFAULT_CONFIG, type MatchConfig } from './types.js';

/**
 * The rules of a match, in plain English.
 *
 * The lobby used to list the settings as chips — `dumping on`, `3 checks
 * each`, `no forced draw` — which named the setting rather than saying what it
 * did. Somebody who had not read the code could not tell what any of them
 * meant, and one of them was a double negative.
 *
 * So the rules are written out as sentences instead, and the numbers a host
 * can change are marked up inside those sentences. That way the same text
 * teaches the game and shows the settings, and a player can see at a glance
 * which parts of it are somebody's choice rather than a law of the game.
 *
 * This lives in the engine because it is derived from the configuration and
 * nothing else, and because it is worth testing: copy that drifts out of step
 * with the rules is worse than no copy.
 */

/** A marked-up number or choice: the bit a host controls. */
export interface RuleValue {
  readonly value: string;
  /** Which setting this came from, for a tooltip. */
  readonly setting: string;
}

export type RulePart = string | RuleValue;

export interface RuleLine {
  /** Grouping, so the lobby can show the essentials and fold the rest away. */
  readonly topic: 'basics' | 'words' | 'calling' | 'scoring' | 'helping' | 'watching' | 'clock';
  readonly parts: readonly RulePart[];
}

const v = (value: string | number, setting: string): RuleValue =>
  ({ value: String(value), setting });

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

const mins = (ms: number): string => {
  const m = ms / 60000;
  return plural(Number.isInteger(m) ? m : Math.round(m * 10) / 10, 'minute');
};

const secs = (ms: number): string => plural(Math.round(ms / 1000), 'second');

const LIST_NAMES: Record<MatchConfig['wordList'], string> = {
  enable: 'ENABLE',
  wordnik: 'Wordnik',
  wiktionary: 'Family',
  custom: 'every bundled list at once',
};

/**
 * Write out one match's rules.
 *
 * `seatCount` is only used where the rules genuinely depend on it — the stuck
 * vote needs everybody, and "everybody" is a number.
 */
export function describeRules(
  config: MatchConfig = DEFAULT_CONFIG,
  seatCount = 0,
): RuleLine[] {
  const c = config;
  const out: RuleLine[] = [];
  const line = (topic: RuleLine['topic'], ...parts: RulePart[]): void => {
    out.push({ topic, parts });
  };

  // ---- the shape of it -------------------------------------------------
  line('basics',
    'Everyone starts at the same moment with ',
    v(plural(c.startingTiles, 'letter'), 'Starting letters'),
    ' and builds their own crossword. There are no turns and nobody waits for anybody.');
  if (seatCount > 1) {
    line('basics',
      c.countdownSec > 0 ? 'When the host starts, a ' : 'When the host starts, play begins ',
      v(c.countdownSec > 0 ? `${c.countdownSec}-second countdown` : 'at once, with no countdown',
        'Countdown before the start'),
      c.countdownSec > 0 ? ' makes sure everyone begins at the same moment.' : '.');
  }

  // ---- what counts as a word -------------------------------------------
  line('words',
    'Words read left to right and top to bottom. Everything you place has to ' +
    'join up into one connected block — two separate groups are not allowed. ' +
    'Letters that touch in a line are read as one word, so CAR written right ' +
    'up against MUCH reads “carmuch”, which is not a word: cross the words, or ' +
    'rearrange them, so every line is a word and the block stays joined.');
  line('words',
    'Every word has to be at least ',
    v(plural(c.minWordLength, 'letter'), 'Shortest word allowed'),
    ' long and in the ',
    v(LIST_NAMES[c.wordList], 'Word list'),
    ' word list, with ',
    v({ both: 'British and American spellings both', british: 'only British spellings',
      american: 'only American spellings' }[c.spelling] ?? 'British and American spellings both',
      'Spelling accepted'),
    c.spelling === 'british' ? ' (colour, centre, realise) accepted.'
      : c.spelling === 'american' ? ' (color, center, realize) accepted.'
      : ' accepted — colour or color, centre or center.');

  // ---- the call ---------------------------------------------------------
  line('calling',
    'The moment your rack is empty and every word is real, call ',
    v(BRAND.callPhrase(c.drawPerRound), 'Letters per round'),
    '. Everyone at the table — you included — then draws ',
    v(c.drawPerRound, 'Letters per round'),
    ' more and carries straight on.');
  line('calling',
    'Calling on a grid that is not finished costs you ',
    c.peek.penalty === 'cooldown'
      // "a 15 seconds wait" — an attributive number takes the singular.
      ? v(`a ${Math.round(c.peek.cooldownMs / 1000)}-second wait`,
          'Calling too early costs')
      : c.peek.penalty === 'drawTiles'
        ? v(plural(c.peek.drawPenalty, 'extra letter'), 'Calling too early costs')
        : v('nothing', 'Calling too early costs'),
    '. Nothing else is taken away, and it clears on its own.');

  // ---- checking ---------------------------------------------------------
  if (c.peek.mode === 'limited') {
    const parts: RulePart[] = [
      'You can ask the word list whether your words are real ',
      v(plural(c.peek.budget, 'time'), 'Checks to start with'),
      ', plus ',
      v(plural(c.peek.budgetPerRound, 'more'), 'Extra checks per round'),
      ' each round. They are rationed on purpose: without a limit the game ' +
      'becomes typing letters until something turns green.',
    ];
    if (c.peek.windowMs > 0) {
      parts.push(' A check is only offered within ',
        v(secs(c.peek.windowMs), 'Check window'),
        ' of moving a tile, so it confirms a hunch rather than going fishing.');
    }
    line('helping', ...parts);
  } else if (c.peek.mode === 'live') {
    line('helping',
      'Your words are marked as you build them — ',
      v('green for real, red for not', 'When can players check?'),
      '. There is no limit on it in this match.');
  } else {
    line('helping',
      'You get ',
      v('no word checking at all', 'When can players check?'),
      ' in this match. You find out when you call.');
  }

  // ---- rescues ----------------------------------------------------------
  line('helping',
    'If your letters simply cannot be played, say you are stuck. When ',
    v(seatCount > 1 ? `all ${seatCount} of you` : 'everybody', 'Always available'),
    ' agree, the whole table draws and play carries on.');
  if (c.dumpEnabled) {
    line('helping',
      'You can also hold a letter in your rack to put it back and draw ',
      v(plural(c.dumpRatio, 'letter'), 'Letters received per trade'),
      ' in its place — you end up with more letters than you started with, ' +
      'which is the price.');
  } else {
    line('helping',
      'Trading a letter back into the bag for more is ',
      v('switched off', 'Allow trading letters in'),
      ' in this match.');
  }

  // ---- the end ----------------------------------------------------------
  line('scoring',
    'The bag holds exactly ',
    v(plural(c.rounds, 'round'), 'Draw rounds'),
    ' for everybody. When it can no longer go round, the next player to use ' +
    'every letter ends the match',
    c.goingOutBonus > 0
      ? ' and takes ' : ' — with ',
    c.goingOutBonus > 0
      ? v(`${c.goingOutBonus} points`, 'Bonus for going out')
      : v('no bonus', 'Bonus for going out'),
    c.goingOutBonus > 0 ? ' for it.' : ' for doing it.');
  line('scoring',
    'Every letter is worth its face value once for each word it is in, so a ' +
    'letter where two words cross is paid twice. Letters you never used come ' +
    'off again. The highest score wins.');
  line('scoring',
    'Your score during the match is ',
    v(c.showLiveScore ? 'shown in the top bar' : 'hidden until the end',
      'Show each player their score'),
    '.');

  // ---- watching ---------------------------------------------------------
  line('watching',
    'Other players’ grids are ',
    v({
      always: 'always on show',
      onRequest: 'yours to look at whenever you like — tap a name at the foot of the board',
      afterFinish: 'visible once they have finished',
      never: 'private until the match ends',
    }[c.visibility.grids], 'Looking at other players’ grids during the match'),
    c.visibility.rackLetters
      ? ', and you can see the actual letters in their hands.'
      : c.visibility.rackCounts
        ? ', and you can see how many letters they are holding.'
        : ', and how many letters they hold is hidden too.');

  // ---- clocks -----------------------------------------------------------
  if (c.forceDrawMs > 0) {
    line('clock',
      'If nobody has called for ',
      v(mins(c.forceDrawMs), 'Force a draw every'),
      ', everyone draws anyway, so one hard rack cannot stall the table.');
  }
  line('clock',
    c.timeLimitMs > 0
      ? 'The whole match stops after ' : 'There is ',
    c.timeLimitMs > 0
      ? v(mins(c.timeLimitMs), 'Time limit')
      : v('no time limit', 'Time limit'),
    c.timeLimitMs > 0 ? ', and is scored where it stands.' : ' — it ends when the bag does.');

  return out;
}

/** The same, flattened, for a screen reader or a plain-text summary. */
export const rulesAsText = (config?: MatchConfig, seatCount?: number): string[] =>
  describeRules(config, seatCount).map((l) =>
    l.parts.map((p) => (typeof p === 'string' ? p : p.value)).join(''));

/**
 * Settings that differ from the standard game.
 *
 * Shown beside a "use the standard rules" button, so a host can see what they
 * have changed before they throw it away — and so a guest can see at a glance
 * whether this table is playing the normal game or somebody's variant.
 */
export function changedFromDefaults(config: MatchConfig): string[] {
  const d = DEFAULT_CONFIG;
  const out: string[] = [];
  const cmp = (name: string, a: unknown, b: unknown, show: (x: never) => string): void => {
    if (a === b) return;
    out.push(`${name}: ${show(a as never)} (normally ${show(b as never)})`);
  };
  cmp('Starting letters', config.startingTiles, d.startingTiles, String);
  cmp('Draw rounds', config.rounds, d.rounds, String);
  cmp('Letters per round', config.drawPerRound, d.drawPerRound, String);
  cmp('Shortest word', config.minWordLength, d.minWordLength, String);
  cmp('Word list', config.wordList, d.wordList, String);
  cmp('Spelling', config.spelling, d.spelling, String);
  cmp('Word checking', config.peek.mode, d.peek.mode, String);
  cmp('Checks to start with', config.peek.budget, d.peek.budget, String);
  cmp('Going-out bonus', config.goingOutBonus, d.goingOutBonus, String);
  cmp('Countdown', config.countdownSec, d.countdownSec, (x: never) => `${x as unknown as number}s`);
  cmp('Score on show', config.showLiveScore, d.showLiveScore,
    (x: never) => ((x as unknown as boolean) ? 'yes' : 'no'));
  cmp('Trading letters in', config.dumpEnabled, d.dumpEnabled,
    (x: never) => ((x as unknown as boolean) ? 'allowed' : 'off'));
  cmp('Looking at other grids mid-match', config.visibility.grids, d.visibility.grids, String);
  cmp('Time limit', config.timeLimitMs, d.timeLimitMs,
    (x: never) => ((x as unknown as number) > 0
      ? `${(x as unknown as number) / 60000} min` : 'none'));
  cmp('Forced draw', config.forceDrawMs, d.forceDrawMs,
    (x: never) => ((x as unknown as number) > 0
      ? `every ${(x as unknown as number) / 60000} min` : 'off'));
  return out;
}
